import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { EmailService } from '../notifications/email.service';
import { isByos } from './platform-storage.constants';
import { StorageConfigService } from './storage-config.service';

const FAILED_STATES = ['auth_failed', 'bucket_not_found', 'permission_denied', 'provider_unavailable', 'config_error'];

/**
 * Periodic connection verification.
 *
 * Daily, and **BYOS configs only** — platform storage is one bucket that one
 * global check covers, so probing it once per merchant would multiply a single
 * fact by the merchant count and turn a free operation budget into a real
 * bill (plan §8.3).
 *
 * `retained` configs are checked alongside active ones. They are exactly the
 * backends a merchant has stopped thinking about, while still serving live
 * images — a revoked key there breaks a storefront just as thoroughly, and
 * nobody would notice without this.
 */
@Injectable()
export class StorageHealthService {
  private readonly logger = new Logger(StorageHealthService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly configs: StorageConfigService,
    private readonly email?: EmailService
  ) {}

  @Cron('0 4 * * *')
  async verifyAllByosConnections(): Promise<void> {
    const configs = await this.prisma.merchantStorageConfig.findMany({
      where: {
        mode: { in: ['byos_r2', 'byos_s3'] },
        status: { in: ['active', 'retained'] },
      },
      select: { id: true, organizationId: true, healthState: true, status: true, bucket: true },
    });
    if (!configs.length) return;

    let failed = 0;
    for (const config of configs) {
      try {
        const probe = await this.configs.verify(config.id);
        const wasHealthy = !FAILED_STATES.includes(config.healthState);
        const nowFailed = FAILED_STATES.includes(probe.state);

        // Alert on the transition only. Re-mailing a merchant every morning
        // about the same broken bucket trains them to ignore the alert.
        if (wasHealthy && nowFailed) {
          failed += 1;
          await this.alert(config.organizationId, config.bucket, probe.state, probe.message);
        }
      } catch (err) {
        this.logger.warn(`Health check failed for config ${config.id}: ${err}`);
      }
    }

    this.logger.log(`Storage health: checked ${configs.length} BYOS connection(s), ${failed} newly failing`);
  }

  private async alert(
    organizationId: string,
    bucket: string,
    state: string,
    message?: string
  ): Promise<void> {
    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { name: true, email: true },
    });
    if (!org?.email) return;

    await this.email
      ?.sendTemplated('storage_connection_failed', org.email, {
        customerName: org.name,
        businessName: org.name,
        bucket,
        state,
        reason: message ?? state,
      })
      .catch((err: unknown) => this.logger.warn(`Storage alert email failed: ${err}`));
  }

  /** Fleet-level counts for the platform admin dashboard. */
  async fleetHealth() {
    const rows = await this.prisma.merchantStorageConfig.groupBy({
      by: ['healthState'],
      where: { status: { in: ['active', 'retained'] } },
      _count: { _all: true },
    });
    return rows.map(r => ({ state: r.healthState, count: r._count._all }));
  }

  /** Configs a merchant may have forgotten about — plan §23.4. */
  async orgsWithManyRetainedConfigs(threshold = 3) {
    const rows = await this.prisma.merchantStorageConfig.groupBy({
      by: ['organizationId'],
      where: { status: 'retained' },
      _count: { _all: true },
    });
    return rows.filter(r => r._count._all > threshold).map(r => ({
      organizationId: r.organizationId,
      retainedConfigs: r._count._all,
    }));
  }

  static isByosMode(mode: string): boolean {
    return isByos(mode);
  }
}
